/**
 * Phone numbers for any country, via libphonenumber-js. Pure functions, so the
 * formatting can be tested without a browser.
 */
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

/** Digits only, always with a leading +: that is all a phone number is. */
export function toE164(typed: string): string {
  const digits = typed.replace(/\D/g, '').slice(0, 15);
  return digits === '' ? '' : `+${digits}`;
}

/** For showing a number back: "+998 90 123 45 67". */
export function formatPhone(e164: string): string {
  return describePhone(e164).display;
}

export function isValidPhone(e164: string): boolean {
  return e164.length > 4 && isValidPhoneNumber(e164);
}

export function describePhone(e164: string): {
  display: string;
  country: CountryCode | undefined;
  valid: boolean;
} {
  if (e164 === '') return { display: '', country: undefined, valid: false };
  const formatter = new AsYouType();
  const display = formatter.input(e164);
  return { display, country: formatter.getCountry(), valid: isValidPhone(e164) };
}

/** Two regional-indicator letters make a flag: UZ → 🇺🇿. */
export function flag(country: CountryCode): string {
  return String.fromCodePoint(...[...country].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export function countryName(country: CountryCode): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(country) ?? country;
  } catch {
    return country;
  }
}
